import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { AuthenticationStore } from '../../states/auth/auth.store';
import { ApprovalStore } from '../../states/approval/approval.store';
import { EmployeeStore } from '../../states/employee/employee.store';
import { HolidayStore } from '../../states/holiday/holiday.store';
import { OrganizationStore } from '../../states/organization/organization.store';
import { PayrollStore } from '../../states/payroll/payroll.store';
import { RoleStore } from '../../states/role/role.store';
import { UserProfileStore } from '../../states/user-profile/user-profile.store';
import { OrganizationService } from '../../services/organization/organization.service';
import { COMPANY_PROFILE_ROUTE } from '../../../features/organization/company-profile/company-profile.component';

export interface SetupChecklistItem {
  key: string;
  label: string;
  description: string;
  route: string;
  routeLabel: string;
  icon: string;
  status: 'complete' | 'pending' | 'review';
  note: string;
  trackable: boolean;
}

@Injectable({ providedIn: 'root' })
export class SetupAttentionService {
  private readonly authStore = inject(AuthenticationStore);
  private readonly userProfileStore = inject(UserProfileStore);
  private readonly organizationStore = inject(OrganizationStore);
  private readonly payrollStore = inject(PayrollStore);
  private readonly roleStore = inject(RoleStore);
  private readonly approvalPolicyStore = inject(ApprovalStore);
  private readonly employeeStore = inject(EmployeeStore);
  private readonly holidayStore = inject(HolidayStore);
  private readonly orgService = inject(OrganizationService);

  private readonly companyProfileComplete = signal<boolean | null>(null);
  private readonly companyProfileLoading = signal(false);
  private readonly companyProfileNote = signal('Not loaded yet');

  private readonly dismissedForSession = signal(false);
  /** True after loadSetupData has been requested at least once this session. */
  private readonly loadRequested = signal(false);
  /**
   * True once the first setup fetch wave has finished so empty stores are not
   * mistaken for incomplete setup (avoids modal flash on refresh).
   */
  private readonly checklistReady = signal(false);
  readonly modalVisible = signal(false);

  private readonly storesLoading = computed(
    () =>
      this.organizationStore.loading() ||
      this.payrollStore.loading() ||
      this.roleStore.loading() ||
      this.approvalPolicyStore.loading() ||
      this.employeeStore.loading() ||
      this.holidayStore.loading() ||
      this.companyProfileLoading() ||
      this.companyProfileComplete() === null,
  );

  readonly loading = computed(() => !this.checklistReady() || this.storesLoading());

  readonly isEligibleUser = computed(() => {
    const profile = this.userProfileStore.userProfile();
    if (!profile) {
      return false;
    }

    if (this.userProfileStore.roleName().trim().toLowerCase() === 'employee') {
      return false;
    }

    return (profile.roleFeatures ?? []).some((feature) =>
      String(feature.routeUrl ?? '').startsWith('/features'),
    );
  });

  readonly checklist = computed((): SetupChecklistItem[] => {
    const departments = this.organizationStore.departments();
    const positions = this.organizationStore.positions();
    const workSchedules = this.organizationStore.workSchedules();
    const holidays = this.holidayStore.holidays();
    const schedules = this.payrollStore.schedules();
    const periods = this.payrollStore.periods();
    const contributionSettings = this.payrollStore.contributionSettings();
    const allowanceTypes = this.payrollStore.allowanceTypes();
    const roles = this.roleStore.roles();
    const policies = this.approvalPolicyStore.policies();
    const departmentPolicies = this.approvalPolicyStore.departmentPolicies();
    const employees = this.employeeStore.employees();

    const hasDepartmentAssignments = departmentPolicies.some((department) =>
      department.policies.some((policy) => !!policy.policyGuid),
    );

    const readyForPayroll =
      departments.length > 0 &&
      positions.length > 0 &&
      workSchedules.length > 0 &&
      holidays.length > 0 &&
      schedules.length > 0 &&
      periods.length > 0 &&
      contributionSettings.length >= 3 &&
      employees.length > 0;

    return [
      {
        key: 'company-profile',
        label: 'Company Profile',
        description: 'Employer name, TIN, and SSS / PhilHealth / Pag-IBIG numbers — required for government reports.',
        route: COMPANY_PROFILE_ROUTE,
        routeLabel: 'Open Company Profile',
        icon: 'pi-building-columns',
        status: this.companyProfileComplete() === true
          ? 'complete'
          : this.companyProfileComplete() === null
            ? 'review'
            : 'pending',
        note: this.companyProfileComplete() === null ? 'Checking…' : this.companyProfileNote(),
        trackable: true,
      },
      {
        key: 'org-structure',
        label: 'Departments & Positions',
        description: 'Create reporting structure used for employees and approvals.',
        route: '/features/hr/departments',
        routeLabel: 'Open Organization',
        icon: 'pi-sitemap',
        status: departments.length > 0 && positions.length > 0 ? 'complete' : 'pending',
        note: `${departments.length} departments, ${positions.length} positions`,
        trackable: true,
      },
      {
        key: 'work-schedules',
        label: 'Work Schedules',
        description: 'Add schedule templates before assigning employees.',
        route: '/features/hr/work-schedules',
        routeLabel: 'Open Work Schedules',
        icon: 'pi-calendar-clock',
        status: workSchedules.length > 0 ? 'complete' : 'pending',
        note: `${workSchedules.length} schedule${workSchedules.length === 1 ? '' : 's'} configured`,
        trackable: true,
      },
      {
        key: 'holidays',
        label: 'Holiday Calendar',
        description: 'Maintain the holiday master table for attendance and payroll.',
        route: '/features/hr/holidays',
        routeLabel: 'Open Holidays',
        icon: 'pi-calendar',
        status: holidays.length > 0 ? 'complete' : 'pending',
        note: `${holidays.length} holiday${holidays.length === 1 ? '' : 's'} loaded`,
        trackable: true,
      },
      {
        key: 'payroll-schedules',
        label: 'Payroll Schedules & Periods',
        description: 'Define pay cycles and generate payroll periods before processing runs.',
        route: '/features/payroll/settings',
        routeLabel: 'Open Payroll Settings',
        icon: 'pi-wallet',
        status: schedules.length > 0 && periods.length > 0 ? 'complete' : 'pending',
        note: `${schedules.length} schedules, ${periods.length} periods`,
        trackable: true,
      },
      {
        key: 'contributions',
        label: 'Contribution Rates',
        description: 'Configure SSS, PhilHealth, and Pag-IBIG rates.',
        route: '/features/payroll/settings',
        routeLabel: 'Review Rates',
        icon: 'pi-percentage',
        status: contributionSettings.length >= 3 ? 'complete' : 'pending',
        note: `${contributionSettings.length} contribution setting${contributionSettings.length === 1 ? '' : 's'} available`,
        trackable: true,
      },
      {
        key: 'compensation-types',
        label: 'Allowance Types',
        description: 'Create earning types (e.g. transportation), then assign amounts on each employee.',
        route: '/features/payroll/settings?tab=allowances',
        routeLabel: 'Review Types',
        icon: 'pi-wallet',
        status: allowanceTypes.length > 0 ? 'complete' : 'pending',
        note: `${allowanceTypes.length} allowance type${allowanceTypes.length === 1 ? '' : 's'}`,
        trackable: true,
      },
      {
        key: 'roles',
        label: 'Roles & Permissions',
        description: 'Prepare admin and HR access before rollout.',
        route: '/features/admin/roles',
        routeLabel: 'Open Roles',
        icon: 'pi-shield',
        status: roles.length > 0 ? 'complete' : 'pending',
        note: `${roles.length} role${roles.length === 1 ? '' : 's'} available`,
        trackable: true,
      },
      {
        key: 'approval-policies',
        label: 'Approval Policies',
        description: 'Create approval flows and assign them to departments.',
        route: '/features/admin/approval-settings',
        routeLabel: 'Open Approval Settings',
        icon: 'pi-check-square',
        status: policies.length > 0 && hasDepartmentAssignments ? 'complete' : 'pending',
        note: `${policies.length} policies, ${departmentPolicies.length} department assignment${departmentPolicies.length === 1 ? '' : 's'}`,
        trackable: true,
      },
      {
        key: 'employees',
        label: 'Employees',
        description: 'Add employees once the organization and payroll masters are ready.',
        route: '/features/hr/employees',
        routeLabel: 'Open Employees',
        icon: 'pi-users',
        status: employees.length > 0 ? 'complete' : 'pending',
        note: `${employees.length} employee${employees.length === 1 ? '' : 's'} in the system`,
        trackable: true,
      },
      {
        key: 'payroll-readiness',
        label: 'Ready for Payroll Run',
        description: 'Use this final check before processing the first payroll cycle.',
        route: '/features/payroll/runs',
        routeLabel: 'Open Payroll Runs',
        icon: 'pi-play-circle',
        status: readyForPayroll ? 'complete' : 'pending',
        note: readyForPayroll ? 'Core prerequisites are in place' : 'Finish the pending setup items first',
        trackable: true,
      },
    ];
  });

  readonly pendingItems = computed(() =>
    this.checklist().filter((item) => item.status !== 'complete'),
  );

  readonly trackedItems = computed(() =>
    this.checklist().filter((item) => item.trackable),
  );

  readonly completedCount = computed(() =>
    this.trackedItems().filter((item) => item.status === 'complete').length,
  );

  readonly progressPercent = computed(() => {
    const total = this.trackedItems().length;
    return total > 0 ? Math.round((this.completedCount() / total) * 100) : 0;
  });

  readonly hasIncompleteSetup = computed(() =>
    this.trackedItems().some((item) => item.status !== 'complete'),
  );

  readonly pendingCount = computed(() => this.pendingItems().length);

  readonly showSetupButton = computed(
    () => this.isEligibleUser() && !this.loading() && this.hasIncompleteSetup(),
  );

  readonly nextSteps = computed(() => this.pendingItems().slice(0, 3));

  constructor() {
    effect(() => {
      if (!this.authStore.isAuthenticated()) {
        return;
      }
      if (!this.userProfileStore.userProfile()) {
        return;
      }
      if (this.loadRequested()) {
        return;
      }
      this.loadSetupData();
    });

    effect(() => {
      if (!this.loadRequested()) {
        return;
      }
      // Mark ready only after company profile resolved and store fetches settled.
      if (!this.storesLoading()) {
        this.checklistReady.set(true);
      }
    });

    effect(() => {
      if (!this.authStore.isAuthenticated()) {
        this.dismissedForSession.set(false);
        this.modalVisible.set(false);
        this.loadRequested.set(false);
        this.checklistReady.set(false);
        this.companyProfileComplete.set(null);
        return;
      }

      // Wait until setup data is loaded — empty arrays look "pending" before that.
      if (!this.checklistReady()) {
        this.modalVisible.set(false);
        return;
      }

      if (!this.hasIncompleteSetup()) {
        this.modalVisible.set(false);
        return;
      }

      if (this.isEligibleUser() && !this.dismissedForSession()) {
        this.modalVisible.set(true);
      }
    });
  }

  loadSetupData(): void {
    if (!this.isEligibleUser()) {
      this.loadRequested.set(true);
      this.checklistReady.set(true);
      return;
    }

    this.loadRequested.set(true);
    this.checklistReady.set(false);
    this.companyProfileComplete.set(null);
    this.loadCompanyProfile();
    this.organizationStore.loadOrganizationData();
    this.payrollStore.loadConfigData();
    this.roleStore.loadRoles();
    this.approvalPolicyStore.loadPolicies();
    this.approvalPolicyStore.loadDepartmentPolicies();
    this.employeeStore.loadEmployees();
    this.holidayStore.loadHolidays(new Date().getFullYear());
  }

  refreshCompanyProfile(): void {
    this.loadCompanyProfile();
  }

  private loadCompanyProfile(): void {
    this.companyProfileLoading.set(true);
    this.orgService.getAgencyProfile().subscribe({
      next: (res) => {
        const p = res.profile;
        const complete = p?.hasStatutoryGaps === false || (
          !!p?.companyName?.trim() &&
          !!p?.tin?.trim() &&
          !!p?.sssNo?.trim() &&
          !!p?.philHealthNo?.trim() &&
          !!p?.pagIbigNo?.trim()
        );
        this.companyProfileComplete.set(complete);
        this.companyProfileNote.set(
          complete
            ? 'Employer details ready for reports'
            : 'Required for SSS, PhilHealth, Pag-IBIG, and BIR forms',
        );
        this.companyProfileLoading.set(false);
      },
      error: () => {
        this.companyProfileComplete.set(false);
        this.companyProfileNote.set('Could not verify company profile');
        this.companyProfileLoading.set(false);
      },
    });
  }

  closeForSession(): void {
    this.dismissedForSession.set(true);
    this.modalVisible.set(false);
  }

  hideModal(): void {
    this.modalVisible.set(false);
  }

  showModal(): void {
    if (this.isEligibleUser() && this.hasIncompleteSetup()) {
      this.dismissedForSession.set(false);
      this.modalVisible.set(true);
    }
  }

  setupStatusLabel(status: SetupChecklistItem['status']): string {
    if (status === 'complete') return 'Complete';
    if (status === 'review') return 'Review';
    return 'Pending';
  }

  setupStatusClass(status: SetupChecklistItem['status']): string {
    if (status === 'complete') return 'badge badge-success';
    if (status === 'review') return 'badge badge-warning';
    return 'badge badge-secondary';
  }
}
